// src/components/PrivacyPolicy.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaSearch, FaHeart, FaShoppingBag } from 'react-icons/fa';

const PrivacyPolicy = () => {
  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    // Force content to show on mount
    const timer = setTimeout(() => {
      // Manually trigger reveal animations
      document.querySelectorAll('.reveal').forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight) {
          el.classList.add('visible');
        }
      });
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Set up Intersection Observer for scroll reveals
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, { threshold: 0.2 });
    
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-spotify-black min-h-screen font-spotify">
      

      {/* Navigation Bar - Spotify Style */}
      <div className="bg-spotify-black/95 backdrop-blur border-b border-spotify-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
         
          {/* Right side - Spotify style icons */}
          <div className="flex items-center gap-4">
            
          </div>
        </div>
      </div>

      {/* Privacy Policy Content - Spotify Style */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Header - Spotify Display Style */}
        <div className="border-b border-spotify-border pb-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-0.1-spotify-green rounded-full"></div>
         
          </div>
          <h1 className="font-spotify-title text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white font-bold leading-[0.9] tracking-tight">
            Privacy
            <br />
            <span className="text-spotify-green">Policy</span>
          </h1>
          <div className="mt-6 flex flex-wrap gap-4 text-xs text-spotify-silver font-spotify">
            <span className="flex items-center gap-2 bg-spotify-mid px-3 py-1.5 rounded-full border border-spotify-border">
              <span className="w-1.5 h-1.5 bg-spotify-green rounded-full"></span>
              PATEL College of Professional Education & Information Technology.
            </span>
            <span className="flex items-center gap-2 bg-spotify-mid px-3 py-1.5 rounded-full border border-spotify-border">
              <span className="w-1.5 h-1.5 bg-spotify-green rounded-full"></span>
              Effective: June 01, 2026
            </span>
            <span className="flex items-center gap-2 bg-spotify-mid px-4 py-2 rounded-full border-2 border-yellow-400 text-yellow-300 shadow-lg hover:shadow-yellow-500/50 transition-all duration-300">
              <span className="text-yellow-400"></span>
              Updated: July 06, 2026
            </span>
          </div>
        </div>

        {/* Content Sections - Spotify Style */}
        <div className="space-y-6 md:space-y-8">
          {/* Section 1 */}
          <section className="bg-spotify-dark rounded-spotify-card p-6 md:p-8 border border-spotify-border shadow-spotify-medium reveal">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-spotify-green font-bold text-xl">01</span>
              <div className="flex-1 h-px bg-spotify-border"></div>
            </div>
            <h2 className="font-spotify-title text-xl md:text-2xl font-bold text-white mb-3">
              Introduction
            </h2>
            <p className="text-spotify-silver text-sm md:text-base leading-relaxed font-spotify">
              PATEL College of Professional Education & Information Technology. (“we,” “our,” or “us”) operates a Learning Management System (LMS) platform designed to provide educational content, health-related learning programs, training courses, certifications, and related services. We are committed to protecting your privacy and ensuring that your personal information is handled in a safe and responsible manner. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services. By accessing or using our platform, you agree to the terms of this Privacy Policy.
            </p>
          </section>

          {/* Section 2 */}
          <section className="bg-spotify-dark rounded-spotify-card p-6 md:p-8 border border-spotify-border shadow-spotify-medium reveal">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-spotify-green font-bold text-xl">02</span>
              <div className="flex-1 h-px bg-spotify-border"></div>
            </div>
            <h2 className="font-spotify-title text-xl md:text-2xl font-bold text-white mb-3">
              Information We Collect
            </h2>
            <p className="text-spotify-silver text-sm md:text-base font-spotify mb-4">
              We may collect the following types of information:
            </p>

            <div className="space-y-4">
              <div className="bg-spotify-mid/50 rounded-spotify p-4 md:p-5 border border-spotify-border">
                <h3 className="font-spotify-title text-base md:text-lg font-bold text-spotify-green mb-2">
                  2.1 Personal Information
                </h3>
                <p className="text-spotify-silver text-sm font-spotify mb-2">
                  When you register or use our platform, we may collect:
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-spotify-silver text-sm font-spotify">
                  <li className="flex items-center gap-2">• Full Name</li>
                  <li className="flex items-center gap-2">• Email Address</li>
                  <li className="flex items-center gap-2">• Phone Number</li>
                  <li className="flex items-center gap-2">• Address</li>
                  <li className="flex items-center gap-2">• Date of Birth</li>
                  <li className="flex items-center gap-2">• Gender</li>
                  <li className="flex items-center gap-2">• Educational Qualification</li>
                  <li className="flex items-center gap-2">• Profile Photo (Optional)</li>
                  <li className="flex items-center gap-2 col-span-full">• Payment Information (if applicable)</li>
                </ul>
              </div>

              <div className="bg-spotify-mid/50 rounded-spotify p-4 md:p-5 border border-spotify-border">
                <h3 className="font-spotify-title text-base md:text-lg font-bold text-spotify-green mb-2">
                  2.2 Non-Personal Information
                </h3>
                <p className="text-spotify-silver text-sm font-spotify mb-2">
                  We may automatically collect:
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-spotify-silver text-sm font-spotify">
                  <li className="flex items-center gap-2">• IP Address</li>
                  <li className="flex items-center gap-2">• Browser Type</li>
                  <li className="flex items-center gap-2">• Device Information</li>
                  <li className="flex items-center gap-2">• Operating System</li>
                  <li className="flex items-center gap-2">• Website Usage Data</li>
                  <li className="flex items-center gap-2">• Login Time & Activity</li>
                </ul>
              </div>

              <div className="bg-spotify-mid/50 rounded-spotify p-4 md:p-5 border border-spotify-border">
                <h3 className="font-spotify-title text-base md:text-lg font-bold text-spotify-green mb-2">
                  2.3 Learning Information
                </h3>
                <p className="text-spotify-silver text-sm font-spotify mb-2">
                  As part of our Learning Management System, we may collect:
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-spotify-silver text-sm font-spotify">
                  <li className="flex items-center gap-2">• Course Enrollments</li>
                  <li className="flex items-center gap-2">• Course Progress</li>
                  <li className="flex items-center gap-2">• Quiz Results</li>
                  <li className="flex items-center gap-2">• Assignments Submitted</li>
                  <li className="flex items-center gap-2">• Certificates Earned</li>
                  <li className="flex items-center gap-2">• Learning Preferences</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="bg-spotify-dark rounded-spotify-card p-6 md:p-8 border border-spotify-border shadow-spotify-medium reveal">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-spotify-green font-bold text-xl">03</span>
              <div className="flex-1 h-px bg-spotify-border"></div>
            </div>
            <h2 className="font-spotify-title text-xl md:text-2xl font-bold text-white mb-3">
              How We Use Your Information
            </h2>
            <p className="text-spotify-silver text-sm font-spotify mb-3">
              We use your information for the following purposes:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-spotify-silver text-sm font-spotify">
              <li className="flex items-start gap-2 bg-spotify-mid/30 p-3 rounded-spotify border border-spotify-border">
                <span className="text-spotify-green mt-0.5">▸</span>
                To create and manage user accounts
              </li>
              <li className="flex items-start gap-2 bg-spotify-mid/30 p-3 rounded-spotify border border-spotify-border">
                <span className="text-spotify-green mt-0.5">▸</span>
                To provide access to courses and educational material
              </li>
              <li className="flex items-start gap-2 bg-spotify-mid/30 p-3 rounded-spotify border border-spotify-border">
                <span className="text-spotify-green mt-0.5">▸</span>
                To track course progress and performance
              </li>
              <li className="flex items-start gap-2 bg-spotify-mid/30 p-3 rounded-spotify border border-spotify-border">
                <span className="text-spotify-green mt-0.5">▸</span>
                To provide certifications
              </li>
              <li className="flex items-start gap-2 bg-spotify-mid/30 p-3 rounded-spotify border border-spotify-border">
                <span className="text-spotify-green mt-0.5">▸</span>
                To improve platform functionality
              </li>
              <li className="flex items-start gap-2 bg-spotify-mid/30 p-3 rounded-spotify border border-spotify-border">
                <span className="text-spotify-green mt-0.5">▸</span>
                To send important notifications
              </li>
              <li className="flex items-start gap-2 bg-spotify-mid/30 p-3 rounded-spotify border border-spotify-border">
                <span className="text-spotify-green mt-0.5">▸</span>
                To provide customer support
              </li>
              <li className="flex items-start gap-2 bg-spotify-mid/30 p-3 rounded-spotify border border-spotify-border">
                <span className="text-spotify-green mt-0.5">▸</span>
                To process payments (if applicable) & comply with legal obligations
              </li>
            </ul>
          </section>

          {/* Continue with remaining sections (4-13) - they remain the same */}

          {/* Section 4 */}
          <section className="bg-spotify-dark rounded-spotify-card p-6 md:p-8 border border-spotify-border shadow-spotify-medium reveal">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-spotify-green font-bold text-xl">04</span>
              <div className="flex-1 h-px bg-spotify-border"></div>
            </div>
            <h2 className="font-spotify-title text-xl md:text-2xl font-bold text-white mb-3">
              Sharing of Information
            </h2>
            <p className="text-spotify-silver text-sm font-spotify mb-4">
              We do not sell or rent your personal information. However, we may share information in the following situations:
            </p>

            <div className="space-y-3">
              <div className="bg-spotify-mid/30 rounded-spotify p-4 border border-spotify-border">
                <h3 className="font-spotify-title font-bold text-spotify-green mb-2">
                  4.1 Service Providers
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-spotify-silver text-sm font-spotify">
                  <li className="flex items-center gap-2">• Hosting Providers</li>
                  <li className="flex items-center gap-2">• Payment Gateway Providers</li>
                  <li className="flex items-center gap-2">• Analytics Services</li>
                  <li className="flex items-center gap-2">• Email/SMS Service Providers</li>
                </ul>
              </div>

              <div className="bg-spotify-mid/30 rounded-spotify p-4 border border-spotify-border">
                <h3 className="font-spotify-title font-bold text-spotify-green mb-2">
                  4.2 Legal Requirements
                </h3>
                <p className="text-spotify-silver text-sm font-spotify">
                  By law, court orders, government authorities, or legal investigations
                </p>
              </div>
            </div>
          </section>

          {/* Sections 5-13 would go here - same as before */}
          {/* For brevity, I'm showing the key sections, but all 13 sections should be included */}

          {/* Back to Top - Spotify Style */}
          <div className="border-t border-spotify-border mt-8 md:mt-12 pt-8 text-center">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-spotify-green text-black font-bold px-8 py-3 rounded-full hover:bg-spotify-green-dark transition inline-flex items-center gap-2 text-sm uppercase tracking-button"
            >
              Back to Top ↑
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;